/**
 * ULD历史记录筛选器组件
 * 提供丰富的筛选条件，包括时间范围、事件类型、ULD识别码等
 */

import React, { useState } from "react";
import { Form, Input, Select, Button, Row, Col, Space, DatePicker } from "antd";
import { SearchOutlined, UndoOutlined, UpOutlined, DownOutlined } from "@ant-design/icons";
import { useUldAirports } from "../../hooks/useUldAirports";

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

// 事件类型选项（基于V3模型）
const EVENT_TYPE_OPTIONS = [
  // V3模型标准事件类型
  { value: "LOC_CHANGE", label: "位置变更" },
  { value: "STATUS_CHANGE", label: "状态变更" },
  { value: "TAG_ADD", label: "添加标签" },
  { value: "TAG_REMOVE", label: "移除标签" },
  { value: "TAG_CHANGE", label: "标签管理" },
  { value: "HOLDER_UPDATE", label: "持有者更新" },
  { value: "LOADED", label: "装载" },
  { value: "UNLOADED", label: "卸载" },
  { value: "CORRECTION", label: "数据校正" },
  { value: "STOCK_IN", label: "入库" },
  
  // 扩展事件类型
  { value: "CORE_STATUS_CHANGE", label: "核心状态变更" },
  { value: "REMARKS_UPDATE", label: "备注更新" },
  { value: "DELIVERY", label: "外放给收货人" },
  { value: "DELIVERY_REVOKE", label: "撤销外放" },
  
  // 向后兼容的历史事件类型
  { value: "CREATE", label: "创建ULD" },
  { value: "UPDATE", label: "更新基础信息" },
  { value: "DELETE", label: "删除ULD" },
  { value: "LOCATION_UPDATE", label: "位置变更(旧)" },
  { value: "STATUS_UPDATE", label: "状态变更(旧)" },
  { value: "TAG_MANAGE", label: "标签管理(旧)" },
  { value: "BATCH_CREATE", label: "批量创建" },
  { value: "BATCH_UPDATE", label: "批量更新" },
];

const UldHistoryFilter = ({ onFilter }) => {
  const [form] = Form.useForm();
  const { airportsList, loading: airportsLoading } = useUldAirports();
  const [expanded, setExpanded] = useState(false);

  // 处理筛选表单提交
  const handleFilter = () => {
    const values = form.getFieldsValue();
    
    // 构建筛选参数
    const filters = {};
    
    // 处理ULD识别码筛选条件 - 完全参考ULD综合管理的逻辑
    if (values.uld_identifiers) {
      // 将文本区域的内容按行分割，过滤空行和空白字符
      const identifiers = values.uld_identifiers
        .split(/[\n,;\s]+/)  // 按换行符、逗号、分号或空白字符分割
        .map(id => id.trim())  // 去除每个识别码的前后空白
        .filter(id => id.length > 0);  // 过滤空字符串
      
      if (identifiers.length > 0) {
        // 如果只有一个识别码，使用单个参数
        if (identifiers.length === 1) {
          filters.uld_identifier = identifiers[0];
        } else {
          // 如果有多个识别码，使用后端新支持的uld_identifiers参数
          filters.uld_identifiers = identifiers.join(",");
        }
      }
    }
    
    // 事件类型
    if (values.event_types && values.event_types.length > 0) {
      filters.event_types = values.event_types.join(",");
    }
    
    // 时间范围
    if (values.time_range && values.time_range.length === 2) {
      filters.start_date = values.time_range[0].format('YYYY-MM-DD HH:mm:ss');
      filters.end_date = values.time_range[1].format('YYYY-MM-DD HH:mm:ss');
    }
    
    // 关键词搜索
    if (values.search_keyword) {
      // 如果已经有来自多重识别码的搜索关键词，则合并
      if (filters.search_keyword) {
        filters.search_keyword = `${filters.search_keyword} ${values.search_keyword}`;
      } else {
        filters.search_keyword = values.search_keyword;
      }
    }
    
    // 高级筛选项
    if (values.user_id) {
      filters.user_id = values.user_id;
    }
    if (values.batch_identifier) {
      filters.batch_identifier = values.batch_identifier;
    }
    if (values.airport_iata) {
      filters.airport_iata = values.airport_iata;
    }

    onFilter(filters);
  };

  // 重置筛选
  const handleReset = () => {
    form.resetFields();
    onFilter({});
  };

  // 切换展开/折叠状态
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFilter}>
      {/* 第一行：ULD识别码查询 */}
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <Form.Item
            name="uld_identifiers"
            label="ULD识别码查询"
            style={{ marginBottom: 8 }}
          >
            <TextArea 
              placeholder="输入ULD识别码，支持多个&#10;可用换行、逗号、分号或空格分隔&#10;例如：&#10;PMC0013UTA&#10;PMC0012UTA,AKE11111ZZ&#10;&#10;注意：由于历史记录API限制，多个识别码将转为模糊搜索"
              autoSize={{ minRows: 2, maxRows: 4 }}
              allowClear
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 第二行：事件类型、时间范围、关键词搜索 */}
      <Row gutter={[16, 8]}>
        <Col span={8}>
          <Form.Item
            name="event_types"
            label="事件类型"
            style={{ marginBottom: 8 }}
          >
            <Select
              mode="multiple"
              placeholder="选择事件类型"
              allowClear
            >
              {EVENT_TYPE_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="time_range"
            label="时间范围"
            style={{ marginBottom: 8 }}
          >
            <RangePicker
              showTime
              placeholder={["开始时间", "结束时间"]}
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="search_keyword"
            label="关键词搜索"
            style={{ marginBottom: 8 }}
          >
            <Input
              placeholder="搜索ULD识别码、原因等"
              allowClear
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 展开区域：高级筛选项 */}
      {expanded && (
        <>
          <Row gutter={[16, 8]}>
            <Col span={8}>
              <Form.Item
                name="user_id"
                label="操作用户"
                style={{ marginBottom: 8 }}
              >
                <Input
                  placeholder="输入用户ID"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="batch_identifier"
                label="批次标识"
                style={{ marginBottom: 8 }}
              >
                <Input
                  placeholder="输入批次标识"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="airport_iata"
                label="机场代码"
                style={{ marginBottom: 8 }}
              >
                <Select
                  placeholder="选择机场"
                  allowClear
                  showSearch
                  loading={airportsLoading}
                  filterOption={(input, option) =>
                                                    (option?.children ?? "").toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {airportsList.map(airport => (
                    <Option key={airport.iata_code} value={airport.iata_code}>
                      {airport.iata_code}{(airport.name_cn || airport.name_en) ? ` - ${airport.name_cn || airport.name_en}` : ''}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {/* 操作按钮 */}
      <Row gutter={[16, 8]} style={{ marginTop: 16 }}>
        <Col>
          <Space>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleFilter}>
              查询
            </Button>
            <Button icon={<UndoOutlined />} onClick={handleReset}>
              重置
            </Button>
            <Button
              type="link"
              icon={expanded ? <UpOutlined /> : <DownOutlined />}
              onClick={toggleExpanded}
            >
              {expanded ? "收起" : "展开"}高级筛选
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

export default UldHistoryFilter; 